import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Label,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import Select from 'react-select';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import { getPresignedUrl } from '../helpers/fileUpload';
import { hydrateActiveContext } from '../store/activeContext/actions';
import { currencies } from '../constants/utils';
import { normalizeWorkOrderView, workOrderViewOptions } from '../constants/workOrderViews';

// Mirror of the react-select styling used on the Assets create form so this
// page's currency picker matches the canonical Assets / Work Orders design pattern.
const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
    boxShadow: hasError
      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
      : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
    '&:hover': { borderColor: '#86b7fe' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
    color: state.isSelected || state.isFocused ? 'white' : '#212529',
    '&:active': { backgroundColor: '#2e669a' },
  }),
});

const OrganizationSettings = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // The active org context drives which organization this page edits — NOT
  // the legacy User.organization. Reading from the activeContext slice means
  // a multi-org user editing Settings sees the org they actually switched into.
  const activeOrganizationId = useSelector(
    (s) => s.ActiveContext?.activeOrganizationId
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [defaultWorkOrderView, setDefaultWorkOrderView] = useState('REACTIVE');
  const [defaultViewSaving, setDefaultViewSaving] = useState(false);
  const [error, setError] = useState('');

  const [orgId, setOrgId] = useState(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [companyIdNumber, setCompanyIdNumber] = useState('');
  const [defaultTaxRate, setDefaultTaxRate] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Currency: persisted org currency lives in `currency`; a pending selection awaiting
  // admin confirmation lives in `pendingCurrency`. Changes go through a dedicated endpoint
  // (NOT /organization/update) so existing cost values are not silently converted.
  const [currency, setCurrency] = useState('USD');
  const [pendingCurrency, setPendingCurrency] = useState(null);
  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [currencySaving, setCurrencySaving] = useState(false);

  const fetchOrganization = useCallback(async () => {
    setLoading(true);
    try {
      // Single source of truth: the active-context Redux slice. Fetch the
      // active org directly by id so multi-org users see the org they
      // switched into — `organization/all` was previously scoped to the
      // user's primary-org binding and returned an empty list for
      // switched-into orgs, leaving this form blank.
      const organizationId = activeOrganizationId;
      if (!organizationId) {
        setLoading(false);
        return;
      }

      const { data } = await api.get(`organization/get-by-id`, {
        params: { id: organizationId },
      });
      const myOrg = data?.response;

      if (myOrg) {
        setOrgId(myOrg.id);
        setName(myOrg.name || '');
        setAddress(myOrg.address || '');
        setEmail(myOrg.email || '');
        setContactNumber(myOrg.contactNumber || '');
        setCompanyIdNumber(myOrg.companyIdNumber || '');
        setDefaultTaxRate(myOrg.defaultTaxRate ?? '');
        setLogoUrl(myOrg.profileUrl || '');
        setCurrency(myOrg.currency || 'USD');
        setDefaultWorkOrderView(normalizeWorkOrderView(myOrg.defaultWorkOrderView) ?? 'REACTIVE');
      }
    } catch (err) {
      console.error('Failed to fetch organization:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrganizationId]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    document.title = `${t('shared.organization', 'Organization')} | Arcmedix`;
  }, [t]);

  // Reuse the curated currency set from the Assets create form so the dropdown vocabulary
  // (codes, localized names, formatting) stays consistent across the product.
  const currencyMap = useMemo(() => currencies(t), [t, i18n.language]);
  const currencyOptions = useMemo(
    () => Object.entries(currencyMap).map(([code, label]) => ({ value: code, label })),
    [currencyMap]
  );
  const currencyValue = useMemo(
    () => ({ value: currency, label: currencyMap[currency] || currency }),
    [currency, currencyMap]
  );

  const handleCurrencySelect = (selected) => {
    const next = selected?.value;
    if (!next || next === currency) {
      return;
    }
    setPendingCurrency(next);
    setCurrencyModalOpen(true);
  };

  const handleCurrencyCancel = () => {
    setPendingCurrency(null);
    setCurrencyModalOpen(false);
  };

  const handleDefaultWorkOrderViewChange = async (e) => {
    const next = normalizeWorkOrderView(e.target.value);
    if (!next || next === defaultWorkOrderView) return;
    const previous = defaultWorkOrderView;
    setDefaultWorkOrderView(next);
    setDefaultViewSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.post('organization/settings/work-order-default-view', {
        defaultView: next,
      });
      const saved = normalizeWorkOrderView(data?.response?.defaultWorkOrderView) ?? next;
      setDefaultWorkOrderView(saved);

      // Mirror into authUser so the Work Orders page picks up the new default without a
      // re-login, and so the active-context slice (which snapshots from localStorage) agrees.
      try {
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        authUser.activeOrganizationDefaultWorkOrderView = saved;
        localStorage.setItem('authUser', JSON.stringify(authUser));
      } catch {
        // Storage failures are non-fatal — the page state already shows the new value.
      }
      dispatch(hydrateActiveContext());

      setSuccess(t('shared.default_work_order_view_saved', 'Default work order view updated.'));
    } catch (err) {
      setDefaultWorkOrderView(previous);
      setError(
        err.response?.data?.message
          || t('shared.default_work_order_view_error', 'Could not update the default work order view. Please try again.')
      );
    } finally {
      setDefaultViewSaving(false);
    }
  };

  const handleCurrencyConfirm = async () => {
    if (!pendingCurrency || pendingCurrency === currency) {
      handleCurrencyCancel();
      return;
    }
    setCurrencySaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.post('organization/settings/currency', {
        currency: pendingCurrency,
      });
      const updated = data?.response;
      const newCurrency = updated?.currency || pendingCurrency;
      setCurrency(newCurrency);

      // Mirror into authUser so a page reload picks up the new currency without a re-login,
      // and so the active-context slice (which snapshots from localStorage) reflects it.
      try {
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        authUser.activeOrganizationCurrency = newCurrency;
        localStorage.setItem('authUser', JSON.stringify(authUser));
      } catch {
        // Storage failures are non-fatal — the page state already shows the new value.
      }
      dispatch(hydrateActiveContext());

      setSuccess(t('shared.currency_change_success', 'Currency updated. Existing values were not converted.'));
      setCurrencyModalOpen(false);
      setPendingCurrency(null);
    } catch (err) {
      setError(
        err.response?.data?.message
          || t('shared.currency_change_error', 'Could not update currency. Please try again.')
      );
    } finally {
      setCurrencySaving(false);
    }
  };

  const hasRealLogo = logoUrl && !logoUrl.includes('profile.png');
  const displayLogo = logoPreview || (hasRealLogo ? logoUrl : null);

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let finalLogoUrl = logoUrl;

      if (pendingLogoFile) {
        const publicUrl = await getPresignedUrl(pendingLogoFile);
        if (publicUrl) {
          finalLogoUrl = publicUrl;
        } else {
          setError(t('shared.failed_upload', 'Failed to upload files. Please try again.'));
          setSaving(false);
          return;
        }
      }

      await api.post('organization/update', {
        id: orgId,
        name,
        address,
        email,
        contactNumber,
        companyIdNumber: companyIdNumber || null,
        defaultTaxRate: defaultTaxRate === '' ? null : Number(defaultTaxRate),
        profileUrl: finalLogoUrl,
      });

      setLogoUrl(finalLogoUrl);
      setPendingLogoFile(null);
      setLogoPreview(null);

      // Sync localStorage so the topbar / switcher / settings reflect the new
      // name without a hard refresh. Updates the active-context name and the
      // membership row for this org.
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      if (authUser.activeOrganizationId === orgId) {
        authUser.activeContextName = name;
      }
      if (Array.isArray(authUser.memberships)) {
        authUser.memberships = authUser.memberships.map((m) =>
          m.organizationId === orgId ? { ...m, organizationName: name } : m
        );
      }
      localStorage.setItem('authUser', JSON.stringify(authUser));
      dispatch(hydrateActiveContext());

      setSuccess(t('shared.profile_updated_successfully', 'Profile updated successfully'));
    } catch (err) {
      setError(err.response?.data?.message || t('shared.something_went_wrong', 'Something went wrong!'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5">
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-4">
                <FeatherIcon icon="briefcase" size={18} className="me-2" />
                {t('shared.organization_information', 'Organization Information')}
              </h5>

              {/* Logo */}
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="position-relative flex-shrink-0"
                  style={{ cursor: 'pointer' }}
                  onClick={handleLogoClick}
                >
                  {displayLogo ? (
                    <img
                      src={displayLogo}
                      alt={name}
                      className="rounded"
                      style={{ width: 72, height: 72, objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded d-flex align-items-center justify-content-center"
                      style={{
                        width: 72,
                        height: 72,
                        backgroundColor: '#3980c0',
                        color: '#fff',
                        fontSize: '1.4rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {name ? name.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                  <div
                    className="position-absolute d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      bottom: -2,
                      right: -2,
                      width: 26,
                      height: 26,
                      backgroundColor: '#fff',
                      border: '2px solid #e0e7ef',
                    }}
                  >
                    <FeatherIcon icon="camera" size={13} className="text-muted" />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleLogoChange}
                />
                <div>
                  <div className="fw-semibold">{name}</div>
                </div>
              </div>

              {success && <Alert color="success" className="mb-3">{success}</Alert>}
              {error && <Alert color="danger" className="mb-3">{error}</Alert>}

              <Row>
                <Col lg={9}>
                  <div className="mb-3">
                    <Label>{t('shared.organization_name', 'Organization Name')}</Label>
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.address', 'Address')}</Label>
                    <Input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.organization_email', 'Organization Email')}</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.organization_contact', 'Organization Contact')}</Label>
                    <Input
                      type="text"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('planning_purchasing.company_id', 'Company ID')}</Label>
                    <Input
                      type="text"
                      value={companyIdNumber}
                      onChange={(e) => setCompanyIdNumber(e.target.value)}
                    />
                    <small className="text-muted">
                      {t('shared.company_id_hint', 'Shown as the buyer identification on purchase order documents.')}
                    </small>
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.default_tax_rate', 'Default tax rate (%)')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={defaultTaxRate}
                      onChange={(e) => setDefaultTaxRate(e.target.value)}
                    />
                    <small className="text-muted">
                      {t('shared.default_tax_rate_hint', 'Prefills the tax rate on new purchase order lines.')}
                    </small>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="currency" className="form-label fw-medium">
                      {t('shared.currency', 'Currency')}
                    </Label>
                    <Select
                      id="currency"
                      options={currencyOptions}
                      value={currencyValue}
                      onChange={handleCurrencySelect}
                      isSearchable
                      isDisabled={currencySaving}
                      placeholder={t('business.assets.select_currency', 'Select Currency')}
                      classNamePrefix="react-select"
                      styles={selectStyles(false)}
                    />
                    <small className="text-muted">
                      {t(
                        'shared.currency_helper',
                        'Single currency per organization. Changing it does not convert existing cost values.'
                      )}
                    </small>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="defaultWorkOrderView" className="form-label fw-medium">
                      {t('shared.default_work_order_view', 'Default work order view')}
                    </Label>
                    <Input
                      id="defaultWorkOrderView"
                      type="select"
                      value={defaultWorkOrderView}
                      onChange={handleDefaultWorkOrderViewChange}
                      disabled={defaultViewSaving}
                    >
                      {workOrderViewOptions(t).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">
                      {t(
                        'shared.default_work_order_view_hint',
                        'Users in this organization see this view when they open Work Orders. They can switch views at any time.'
                      )}
                    </small>
                  </div>

                  <Button
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? <Spinner size="sm" className="me-1" /> : null}
                    {t('shared.save', 'Save')}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </motion.div>

        <Modal isOpen={currencyModalOpen} toggle={handleCurrencyCancel} backdrop="static">
          <ModalHeader toggle={handleCurrencyCancel}>
            {t('shared.currency_change_confirm_title', 'Change organization currency?')}
          </ModalHeader>
          <ModalBody>
            <p>
              {t(
                'shared.currency_change_confirm_body',
                'Existing cost values will not be converted — only the displayed currency changes.'
              )}
            </p>
            <p className="mb-0">
              <strong>{currency}</strong>
              {' → '}
              <strong>{pendingCurrency}</strong>
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCurrencyCancel} disabled={currencySaving}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="primary" onClick={handleCurrencyConfirm} disabled={currencySaving}>
              {currencySaving ? <Spinner size="sm" className="me-1" /> : null}
              {t('shared.currency_change_confirm_button', 'Change currency')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default OrganizationSettings;
